/**
 * RecordTypes API Client
 *
 * Handles all HTTP requests to the RecordTypes backend API.
 * Uses AuthenticatedApiClient which is self-sufficient (fetches auth dynamically).
 *
 * Key Responsibilities:
 * - Make authenticated requests to /api/v1/record-types endpoints
 * - Transform API responses from snake_case to camelCase
 * - Handle errors with user-friendly messages
 * - Support filtering, pagination, and advanced queries
 *
 * Usage:
 * ```typescript
 * // Production (no token needed - self-sufficient)
 * const api = getRecordTypesApi();
 * const recordTypes = await api.getRecordTypes({ status: 'published' });
 *
 * // Testing (inject mock token)
 * const api = getRecordTypesApi('mock-token');
 * ```
 */

import {AuthenticatedApiClient} from '@/matic/lib/api/authenticated-api-client';
import type {
  RecordType,
  RecordTypeList,
  RecordTypeResponse,
  RecordTypeListResponse,
  RecordTypeFilters,
  CreateRecordTypeInput,
  UpdateRecordTypeInput,
  RecordTypeQueryRequest,
  RecordTypeRevision,
  RecordTypeRevisionResponse,
  RecordTypeRevisionListResponse,
  FieldDefinition,
  FieldDefinitionResponse,
  ConditionRule,
  ConditionRuleResponse,
  WorkflowStepDefinition,
  WorkflowStepDefinitionResponse,
  ListColumnConfig,
  ListColumnConfigResponse,
} from '../types';
import type {
  RecordTypeApproval,
  RecordTypeApprovalResponse,
  RecordTypeApprovalStatus,
  RecordTypeApprovalList,
  RecordTypeApprovalListResponse,
  RecordTypeApprovalListItemResponse,
  RecordTypeApprovalListItem,
} from '../types/approval';
import type {AuditTrailEvent} from '@/matic/features/_shared/approvals/types';
import {convertFieldConfigResponse} from '../utils/field-config-transforms';

/**
 * API Client for RecordTypes
 *
 * Extends AuthenticatedApiClient (self-sufficient auth).
 */
export class RecordTypesApiClient extends AuthenticatedApiClient {
  /**
   * Base path for RecordType endpoints
   */
  private get basePath(): string {
    return '/api/v1/record-types';
  }

  /**
   * Get list of RecordTypes with optional filters and pagination
   */
  async getRecordTypes(
    filters: RecordTypeFilters = {}
  ): Promise<RecordTypeList> {
    const parameters = new URLSearchParams();

    if (filters.status) {
      parameters.append('status', filters.status);
    }

    if (filters.search) {
      parameters.append('search', filters.search);
    }

    if (filters.limit !== undefined) {
      parameters.append('limit', filters.limit.toString());
    }

    if (filters.offset !== undefined) {
      parameters.append('offset', filters.offset.toString());
    }

    if (filters.referenceObjectTypeId) {
      parameters.append(
        'reference_object_type_id',
        filters.referenceObjectTypeId
      );
    }

    const endpoint =
      parameters.toString().length > 0
        ? `${this.basePath}?${parameters.toString()}`
        : this.basePath;

    const response = await this.get<RecordTypeListResponse>(endpoint);
    return this.convertRecordTypeListResponse(response);
  }

  /**
   * Query RecordTypes with advanced filtering, sorting, and pagination
   */
  async queryRecordTypes(
    queryRequest: RecordTypeQueryRequest
  ): Promise<RecordTypeList> {
    const response = await this.post<RecordTypeListResponse>(
      `${this.basePath}/query`,
      queryRequest
    );

    return this.convertRecordTypeListResponse(response);
  }

  /**
   * Get single RecordType by ID
   */
  async getRecordType(id: string): Promise<RecordType> {
    const response = await this.get<RecordTypeResponse>(
      `${this.basePath}/${id}`
    );

    return this.convertRecordTypeResponse(response);
  }

  /**
   * Create new RecordType
   *
   * Creates RecordType in DRAFT status with initial revision.
   */
  async createRecordType(input: CreateRecordTypeInput): Promise<RecordType> {
    const requestBody: Record<string, unknown> = {
      name: input.name,
      prefix: input.prefix,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      display_id: input.displayId,
      description: input.description,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      revision_format: input.revisionFormat,
      fields: input.fields.map((f) => this.convertFieldToApi(f)),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      workflow_steps: input.workflowSteps.map((s) =>
        this.convertWorkflowStepToApi(s)
      ),
    };

    if (input.revisionNumberPadding !== undefined) {
      requestBody.revision_number_padding = input.revisionNumberPadding;
    }

    if (input.attachmentsEnabled !== undefined) {
      requestBody.attachments_enabled = input.attachmentsEnabled;
    }

    const response = await this.post<RecordTypeResponse>(
      this.basePath,
      requestBody
    );

    return this.convertRecordTypeResponse(response);
  }

  /**
   * Update existing RecordType
   *
   * - For DRAFT: Updates the draft revision
   * - For PUBLISHED: Creates new draft revision (copy-on-edit)
   */
  async updateRecordType(
    id: string,
    input: UpdateRecordTypeInput
  ): Promise<RecordType> {
    const requestBody: Record<string, unknown> = {};

    if (input.name !== undefined) {
      requestBody.name = input.name;
    }

    if (input.description !== undefined) {
      requestBody.description = input.description;
    }

    if (input.displayId !== undefined) {
      requestBody.display_id = input.displayId;
    }

    if (input.revisionFormat !== undefined) {
      requestBody.revision_format = input.revisionFormat;
    }

    if (input.revisionNumberPadding !== undefined) {
      requestBody.revision_number_padding = input.revisionNumberPadding;
    }

    if (input.fields !== undefined) {
      requestBody.fields = input.fields.map((f) => this.convertFieldToApi(f));
    }

    if (input.workflowSteps !== undefined) {
      requestBody.workflow_steps = input.workflowSteps.map((s) =>
        this.convertWorkflowStepToApi(s)
      );
    }

    if (input.changelog !== undefined) {
      requestBody.changelog = input.changelog;
    }

    if (input.attachmentsEnabled !== undefined) {
      requestBody.attachments_enabled = input.attachmentsEnabled;
    }

    const response = await this.patch<RecordTypeResponse>(
      `${this.basePath}/${id}`,
      requestBody
    );

    return this.convertRecordTypeResponse(response);
  }

  /**
   * Delete RecordType (draft only)
   */
  async deleteRecordType(id: string): Promise<void> {
    await this.delete(`${this.basePath}/${id}`);
  }

  /**
   * Retire RecordType (published only)
   */
  async retireRecordType(id: string): Promise<RecordType> {
    const response = await this.post<RecordTypeResponse>(
      `${this.basePath}/${id}/retire`,
      {}
    );

    return this.convertRecordTypeResponse(response);
  }

  /**
   * Pin or unpin a RecordType
   */
  async pinRecordType(id: string, pinned: boolean): Promise<void> {
    await this.patch(`${this.basePath}/${id}/pin`, {pinned});
  }

  /**
   * Update the list column configuration for a RecordType
   */
  async updateListColumns(
    id: string,
    listColumns: ListColumnConfig[] | undefined
  ): Promise<RecordType> {
    const response = await this.patch<RecordTypeResponse>(
      `${this.basePath}/${id}/list-columns`,
      {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        list_columns: listColumns
          ? listColumns.map((col) => ({
              // eslint-disable-next-line @typescript-eslint/naming-convention
              field_key: col.fieldKey,
              label: col.label,
              width: col.width,
            }))
          : null,
      }
    );

    return this.convertRecordTypeResponse(response);
  }

  /**
   * Export RecordType to PDF
   */
  async exportPdf(recordTypeId: string): Promise<Blob> {
    return this.getBlob(`${this.basePath}/${recordTypeId}/export-pdf`);
  }

  // ============================================================================
  // Revision Management
  // ============================================================================

  /**
   * Get revisions for a RecordType
   */
  async getRecordTypeRevisions(
    recordTypeId: string
  ): Promise<{items: RecordTypeRevision[]; total: number}> {
    const response = await this.get<RecordTypeRevisionListResponse>(
      `${this.basePath}/${recordTypeId}/revisions`
    );

    return {
      items: response.items.map((item) => this.convertRevisionResponse(item)),
      total: response.total,
    };
  }

  /**
   * Get specific revision
   */
  async getRecordTypeRevision(
    recordTypeId: string,
    revisionId: string
  ): Promise<RecordTypeRevision> {
    const response = await this.get<RecordTypeRevisionResponse>(
      `${this.basePath}/${recordTypeId}/revisions/${revisionId}`
    );

    return this.convertRevisionResponse(response);
  }

  /**
   * Discard draft revision
   */
  async discardDraftRevision(
    recordTypeId: string,
    revisionId: string
  ): Promise<RecordType> {
    const response = await this.delete<RecordTypeResponse>(
      `${this.basePath}/${recordTypeId}/revisions/${revisionId}`
    );

    return this.convertRecordTypeResponse(response);
  }

  // ============================================================================
  // Approval Workflow
  // ============================================================================

  /**
   * Submit revision for approval
   * Note: Backend uses record-type-level endpoint, not revision-level
   */
  async submitForApproval(
    recordTypeId: string,
    _revisionId: string,
    approverIds: string[],
    approvalRule: 'all' | 'any' = 'all',
    comment?: string
  ): Promise<RecordType> {
    const response = await this.post<RecordTypeResponse>(
      `${this.basePath}/${recordTypeId}/submit-for-approval`,
      {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        approver_ids: approverIds,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        approval_rule: approvalRule,
        comment,
      }
    );

    return this.convertRecordTypeResponse(response);
  }

  /**
   * Approve revision
   * Note: Backend uses record-type-level endpoint, not revision-level
   */
  async approveRevision(
    recordTypeId: string,
    _revisionId: string,
    comment?: string
  ): Promise<RecordType> {
    const response = await this.post<RecordTypeResponse>(
      `${this.basePath}/${recordTypeId}/approve`,
      {
        comment,
      }
    );

    return this.convertRecordTypeResponse(response);
  }

  /**
   * Reject revision
   * Note: Backend uses record-type-level endpoint, not revision-level
   */
  async rejectRevision(
    recordTypeId: string,
    _revisionId: string,
    comment: string
  ): Promise<RecordType> {
    const response = await this.post<RecordTypeResponse>(
      `${this.basePath}/${recordTypeId}/reject`,
      {
        comment,
      }
    );

    return this.convertRecordTypeResponse(response);
  }

  /**
   * Cancel approval submission
   * Note: Backend endpoint needs to be added: DELETE /record-types/{id}/approval
   */
  async cancelApprovalSubmission(
    recordTypeId: string,
    _revisionId: string
  ): Promise<RecordType> {
    const response = await this.delete<RecordTypeResponse>(
      `${this.basePath}/${recordTypeId}/approval`
    );

    return this.convertRecordTypeResponse(response);
  }

  /**
   * Reassign approvers on a RecordType revision's active approval.
   *
   * Requires the revision to be IN_REVIEW with an active approval.
   * Admin, Quality, or the original submitter can reassign; backend
   * blocks the request once any approver has already signed.
   */
  async reassignApprovers(
    recordTypeId: string,
    revisionId: string,
    approverIds: string[],
    comment?: string
  ): Promise<RecordType> {
    const response = await this.put<RecordTypeResponse>(
      `${this.basePath}/${recordTypeId}/revisions/${revisionId}/approval/approvers`,
      {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        approver_ids: approverIds,
        comment,
      }
    );

    return this.convertRecordTypeResponse(response);
  }

  /**
   * Publish approved revision
   * Note: Backend uses record-type-level endpoint, not revision-level
   */
  async publishRevision(
    recordTypeId: string,
    _revisionId: string
  ): Promise<RecordType> {
    const response = await this.post<RecordTypeResponse>(
      `${this.basePath}/${recordTypeId}/publish`,
      {}
    );

    return this.convertRecordTypeResponse(response);
  }

  /**
   * Get approval status for a RecordType revision
   *
   * Fetches the current approval workflow including assigned approvers.
   * Returns undefined if no approval workflow exists (draft status).
   *
   * Endpoint: GET /record-types/{record_type_id}/revisions/{revision_id}/approval
   */
  async getApprovalStatus(
    recordTypeId: string,
    revisionId: string
  ): Promise<RecordTypeApproval | undefined> {
    try {
      const response = await this.get<RecordTypeApprovalResponse>(
        `${this.basePath}/${recordTypeId}/revisions/${revisionId}/approval`
      );
      return this.convertApprovalResponse(response);
    } catch (error: any) {
      // 404 means no approval workflow exists (draft status)
      if (error?.response?.status === 404) {
        return undefined;
      }

      throw error;
    }
  }

  /**
   * Get pending RecordType approvals for current user
   *
   * Fetches approvals where:
   * - Status is 'in_review'
   * - Current user is assigned as an approver
   *
   * Endpoint: GET /api/v1/records/approvals/pending
   */
  async getPendingApprovals(
    options: {limit?: number; offset?: number} = {}
  ): Promise<RecordTypeApprovalList> {
    const parameters = new URLSearchParams();
    if (options.limit !== undefined) {
      parameters.append('limit', options.limit.toString());
    }

    if (options.offset !== undefined) {
      parameters.append('offset', options.offset.toString());
    }

    const endpoint = `/api/v1/records/approvals/pending${parameters.toString() ? `?${parameters.toString()}` : ''}`;

    try {
      const response = await this.get<RecordTypeApprovalListResponse>(endpoint);
      return {
        items: response.items.map((item) =>
          this.convertPendingApprovalResponse(item)
        ),
        total: response.total,
        limit: response.limit,
        offset: response.offset,
      };
    } catch (error) {
      // If endpoint doesn't exist (404), return empty list (graceful degradation)
      if ((error as {status?: number})?.status === 404) {
        return {
          items: [],
          total: 0,
          limit: options.limit ?? 20,
          offset: options.offset ?? 0,
        };
      }

      throw error;
    }
  }

  // ============================================================================
  // Next Suffix
  // ============================================================================

  /**
   * Get next suggested suffix for a RecordType's display ID
   *
   * Endpoint: GET /api/v1/record-types/{id}/next-suffix
   */
  async getNextSuffix(
    recordTypeId: string
  ): Promise<{suggestedSuffix: string; prefix: string}> {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const response = await this.get<{suggested_suffix: string; prefix: string}>(
      `${this.basePath}/${recordTypeId}/next-suffix`
    );

    return {
      suggestedSuffix: response.suggested_suffix,
      prefix: response.prefix,
    };
  }

  // ============================================================================
  // Private Conversion Methods
  // ============================================================================

  /**
   * Convert RecordType list response from snake_case to camelCase
   */
  private convertRecordTypeListResponse(
    response: RecordTypeListResponse
  ): RecordTypeList {
    return {
      items: response.items.map((item) => this.convertRecordTypeResponse(item)),
      total: response.total,
      limit: response.limit,
      offset: response.offset,
    };
  }

  /**
   * Convert RecordType response from snake_case to camelCase
   */
  private convertRecordTypeResponse(response: RecordTypeResponse): RecordType {
    return {
      id: response.id,
      tenantId: response.tenant_id,
      name: response.name,
      prefix: response.prefix,
      displayId: response.display_id,
      description: response.description,
      revisionFormat: response.revision_format as RecordType['revisionFormat'],
      revisionNumberPadding:
        response.revision_number_padding as RecordType['revisionNumberPadding'],
      status: response.status as RecordType['status'],
      latestRevisionId: response.latest_revision_id,
      latestRevisionNumber: response.latest_revision_number,
      latestRevisionLabel: response.latest_revision_label,
      publishedRevisionId: response.published_revision_id,
      publishedRevisionNumber: response.published_revision_number,
      publishedRevisionLabel: response.published_revision_label,
      fields: response.fields.map((f) => this.convertFieldResponse(f)),
      workflowSteps: response.workflow_steps.map((s) =>
        this.convertWorkflowStepResponse(s)
      ),
      attachmentsEnabled: Boolean(response.attachments_enabled),
      isPinned: Boolean(response.is_pinned),
      listColumns: response.list_columns
        ? response.list_columns.map(
            (col: ListColumnConfigResponse): ListColumnConfig => ({
              fieldKey: col.field_key,
              label: col.label,
              width: col.width,
            })
          )
        : undefined,
      createdBy: response.created_by,
      createdAt: response.created_at,
      updatedAt: response.updated_at,
    };
  }

  /**
   * Convert Revision response from snake_case to camelCase
   */
  private convertRevisionResponse(
    response: RecordTypeRevisionResponse
  ): RecordTypeRevision {
    return {
      id: response.id,
      recordTypeId: response.record_type_id,
      revisionNumber: response.revision_number,
      revisionLabel: response.revision_label,
      changelog: response.changelog,
      contentSnapshot: {
        name: response.content_snapshot.name,
        fields: response.content_snapshot.fields.map((f) =>
          this.convertFieldResponse(f)
        ),
        workflowSteps: response.content_snapshot.workflow_steps.map((s) =>
          this.convertWorkflowStepResponse(s)
        ),
        attachmentsEnabled: Boolean(
          response.content_snapshot.attachments_enabled
        ),
      },
      status: response.status as RecordTypeRevision['status'],
      publishedAt: response.published_at,
      createdBy: response.created_by,
      createdAt: response.created_at,
      updatedAt: response.updated_at,
    };
  }

  /**
   * Convert Field response from backend format (snake_case) to frontend format (camelCase)
   *
   * Frontend FieldDefinition now aligns with backend FieldDefinitionResponse.
   */
  private convertFieldResponse(
    response: FieldDefinitionResponse
  ): FieldDefinition {
    return {
      name: response.field_key,
      label: response.label,
      fieldType: response.field_type as FieldDefinition['fieldType'],
      required: response.required,
      config: response.config ? this.convertConfig(response.config) : undefined,
      sortOrder: response.sort_order,
      description: response.description,
      conditions: response.conditions
        ? this.convertConditionRulesResponse(response.conditions)
        : undefined,
    };
  }

  /**
   * Convert config from snake_case to camelCase
   */
  private convertConfig(
    options: Record<string, unknown>
  ): NonNullable<FieldDefinition['config']> {
    return convertFieldConfigResponse(options);
  }

  /**
   * Convert WorkflowStep response from backend format (snake_case) to frontend format (camelCase)
   *
   * Note: Assignees are now configured at the record level, not template level.
   */
  private convertWorkflowStepResponse(
    response: WorkflowStepDefinitionResponse
  ): WorkflowStepDefinition {
    return {
      title: response.title,
      description: response.description,
      stepOrder: response.step_order,
      fieldKeys: response.field_keys ?? [],
      conditions: response.conditions
        ? this.convertConditionRulesResponse(response.conditions)
        : undefined,
    };
  }

  /**
   * Convert Field to API format (snake_case)
   *
   * Frontend FieldDefinition now aligns with backend FieldDefinitionRequest.
   */
  private convertFieldToApi(field: FieldDefinition): Record<string, unknown> {
    const result: Record<string, unknown> = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      field_key: field.name,
      label: field.label,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      field_type: field.fieldType,
      required: field.required,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      sort_order: field.sortOrder ?? 0,
    };

    if (field.config && Object.keys(field.config).length > 0) {
      result.config = this.convertConfigToApi(field.config);
    }

    if (field.description) {
      result.description = field.description;
    }

    if (field.conditions && field.conditions.length > 0) {
      result.conditions = this.convertConditionRulesToApi(field.conditions);
    }

    return result;
  }

  /**
   * Convert config to API format (snake_case)
   */
  private convertConfigToApi(
    options: NonNullable<FieldDefinition['config']>
  ): Record<string, unknown> {
    const result: Record<string, unknown> = {};

    if (options.maxLength !== undefined) {
      result.max_length = options.maxLength;
    }

    if (options.min !== undefined) {
      result.min = options.min;
    }

    if (options.max !== undefined) {
      result.max = options.max;
    }

    if (options.decimalPlaces !== undefined) {
      result.decimal_places = options.decimalPlaces;
    }

    if (options.options !== undefined) {
      result.options = options.options;
    }

    if (options.roleFilter !== undefined) {
      result.role_filter = options.roleFilter;
    }

    if (options.multiSelect !== undefined) {
      result.multi_select = options.multiSelect;
    }

    if (options.columns !== undefined) {
      // Mirror the top-level object_reference transform for table columns:
      // referenceObjectTypeId → reference_object_type_id (multi stays multi).
      result.columns = options.columns.map((col) => {
        const {referenceObjectTypeId, ...rest} = col;
        if (referenceObjectTypeId === undefined) return rest;
        // eslint-disable-next-line @typescript-eslint/naming-convention
        return {...rest, reference_object_type_id: referenceObjectTypeId};
      });
    }

    if (options.signatureMeaning !== undefined) {
      result.signature_meaning = options.signatureMeaning;
    }

    if (options.maxFiles !== undefined) {
      result.max_files = options.maxFiles;
    }

    if (options.referenceObjectTypeId !== undefined) {
      result.reference_object_type_id = options.referenceObjectTypeId;
    }

    if (options.multi !== undefined) {
      result.multi = options.multi;
    }

    return result;
  }

  /**
   * Convert WorkflowStep to API format (snake_case)
   *
   * Note: Assignees are now configured at the record level, not template level.
   */
  private convertWorkflowStepToApi(
    step: WorkflowStepDefinition
  ): Record<string, unknown> {
    const result: Record<string, unknown> = {
      title: step.title,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      step_order: step.stepOrder,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      field_keys: step.fieldKeys ?? [],
    };

    if (step.description) {
      result.description = step.description;
    }

    if (step.conditions && step.conditions.length > 0) {
      result.conditions = this.convertConditionRulesToApi(step.conditions);
    }

    return result;
  }

  /**
   * Convert condition rules from backend format (snake_case) to frontend format (camelCase)
   */
  private convertConditionRulesResponse(
    rules: ConditionRuleResponse[]
  ): ConditionRule[] {
    return rules.map((rule) => ({
      triggerFieldKey: rule.trigger_field_key,
      operator: rule.operator as ConditionRule['operator'],
      value: rule.value,
      actionType: rule.action_type as ConditionRule['actionType'],
    }));
  }

  /**
   * Convert condition rules to API format (snake_case)
   */
  private convertConditionRulesToApi(
    rules: ConditionRule[]
  ): Array<Record<string, unknown>> {
    return rules.map((rule) => ({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      trigger_field_key: rule.triggerFieldKey,
      operator: rule.operator,
      value: rule.value,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      action_type: rule.actionType,
    }));
  }

  /**
   * Convert Approval response from snake_case to camelCase
   *
   * Also converts backend status to frontend status:
   * - Backend uses: in_review, approved, rejected, cancelled
   * - Frontend uses: pending, approved, rejected, cancelled
   */
  private convertApprovalResponse(
    response: RecordTypeApprovalResponse
  ): RecordTypeApproval {
    // Map backend status to frontend status
    // Backend: in_review -> Frontend: pending
    const statusMap: Record<string, RecordTypeApprovalStatus> = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      in_review: 'pending',
      approved: 'approved',
      rejected: 'rejected',
      cancelled: 'cancelled',
    };

    return {
      id: response.id,
      status:
        statusMap[response.status] ??
        (response.status as RecordTypeApprovalStatus),
      recordTypeId: response.record_type_id,
      revisionId: response.revision_id,
      approverIds: response.approver_ids,
      submittedBy: response.submitted_by,
      createdAt: response.created_at,
      completedAt: response.completed_at,
      auditTrail: response.audit_trail?.map((e) => ({
        id: e.id,
        action: e.action as AuditTrailEvent['action'],
        comment: e.comment,
        createdBy: e.created_by,
        createdAt: e.created_at,
      })),
    };
  }

  /**
   * Convert pending approval list item response from snake_case to camelCase
   */
  private convertPendingApprovalResponse(
    response: RecordTypeApprovalListItemResponse
  ): RecordTypeApprovalListItem {
    return {
      id: response.id,
      status: response.status,
      resourceId: response.resource_id,
      resourceType: response.resource_type,
      recordTypeId: response.record_type_id,
      recordTypeName: response.record_type_name,
      approvalRule: response.approval_rule,
      approverIds: response.approver_ids,
      approvedBy: response.approved_by,
      createdBy: response.created_by,
      createdAt: response.created_at,
      updatedAt: response.updated_at,
      completedAt: response.completed_at,
    };
  }
}

/**
 * Singleton factory for RecordTypesApiClient
 *
 * The client is now self-sufficient (fetches auth headers dynamically),
 * so we use a single cached instance for production.
 * Passing a token creates a fresh instance for testing.
 */
let cachedClient: RecordTypesApiClient | undefined;

export function getRecordTypesApi(token?: string): RecordTypesApiClient {
  // Testing: fresh instance with injected token
  if (token) {
    return new RecordTypesApiClient(token);
  }

  // Production: singleton instance (self-sufficient auth)
  cachedClient ??= new RecordTypesApiClient();

  return cachedClient;
}
